// 이미지 업로더 컴포넌트.
// 입력 방식 두 가지:
//   1) 파일 선택 다이얼로그
//   2) 박스에 포커스를 둔 상태에서 Ctrl+V (클립보드 이미지 붙여넣기)
// 두 경로 모두 동일 uploadImage 를 호출해 R2 에 업로드 후 객체 키를 부모에 콜백한다.
// 다중 이미지가 필요한 화면은 본 컴포넌트를 반복/조합해 처리한다.

import { useRef, useState } from "react";
import type { ImageUploadKind } from "@oo/shared";
import { uploadImage } from "./catalogApi.ts";
import { downscaleImage } from "./resizeImage.ts";
import { imageUrl } from "./imageUrl.ts";

// 용도별 권장(기준) 업로드 크기 안내(개선항목 12). resizeImage.ts 의 긴 변 상한과 일치시킨다.
// 더 큰 이미지를 올려도 업로드 시 자동 축소되지만, 비율/선명도를 위해 기준 크기를 안내한다.
const RECOMMENDED_SIZE: Record<ImageUploadKind, string> = {
  category: "512 x 512 (정사각형)",
  "product-main": "1280 x 1280 (정사각형)",
  "product-detail": "긴 변 1600 (비율 자유)",
  banner: "1600 x 700 (16:7 가로형)",
};

interface Props {
  kind: ImageUploadKind;
  value: string | null;
  onChange: (key: string | null) => void;
  // 미리보기 박스 크기 (Tailwind 클래스). 카테고리 아이콘은 작게, 상품 메인은 크게.
  previewClassName?: string;
  // 비활성화. 폼 전송 중 등.
  disabled?: boolean;
}

// 이미지 업로드 UI 컴포넌트. 파일 선택 -> 업로드 -> R2 키 콜백 + 미리보기.
export default function ImageUploader({ kind, value, onChange, previewClassName, disabled }: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const boxRef = useRef<HTMLDivElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 공통 업로드 처리.
  // 파일 선택/붙여넣기 두 경로에서 모두 호출한다.
  async function runUpload(file: File) {
    setBusy(true);
    setError(null);
    try {
      // 업로드 전 클라이언트에서 축소/webp 재인코딩(전송·저장량 절감). 실패 시 원본 사용.
      const optimized = await downscaleImage(file, kind);
      const uploaded = await uploadImage(optimized, kind);
      onChange(uploaded.key);
    } catch (err) {
      setError(err instanceof Error ? err.message : "업로드 실패");
    } finally {
      setBusy(false);
    }
  }

  async function handleSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = ""; // 같은 파일 재선택 허용
    if (!file) {
      return;
    }
    await runUpload(file);
  }

  // 박스에 포커스가 있을 때만 동작하는 paste 핸들러.
  // clipboardData.files 와 items 양쪽을 모두 검사한다 (브라우저별 차이 대응).
  async function handlePaste(e: React.ClipboardEvent<HTMLDivElement>) {
    if (disabled || busy) {
      return;
    }
    const cd = e.clipboardData;
    if (!cd) {
      return;
    }
    // 1) files 컬렉션 우선 검사 (스크린샷 등 대부분 여기 잡힘).
    if (cd.files && cd.files.length > 0) {
      for (let i = 0; i < cd.files.length; i += 1) {
        const f = cd.files.item(i);
        if (f && f.type.startsWith("image/")) {
          e.preventDefault();
          await runUpload(f);
          return;
        }
      }
    }
    // 2) items 컬렉션 (Firefox 등 일부 환경).
    if (cd.items && cd.items.length > 0) {
      for (let i = 0; i < cd.items.length; i += 1) {
        const item = cd.items[i];
        if (item && item.kind === "file" && item.type.startsWith("image/")) {
          const f = item.getAsFile();
          if (f) {
            e.preventDefault();
            await runUpload(f);
            return;
          }
        }
      }
    }
  }

  const url = imageUrl(value);
  return (
    <div className="flex flex-col gap-2">
      <div
        ref={boxRef}
        tabIndex={disabled ? -1 : 0}
        onPaste={handlePaste}
        onClick={() => boxRef.current?.focus()}
        className={`flex cursor-pointer items-center justify-center overflow-hidden rounded-md border border-dashed border-ink-300 bg-ink-50 outline-none focus:border-brand-600 focus:ring-2 focus:ring-ink-900/20 ${
          previewClassName ?? "h-32 w-32"
        }`}
      >
        {url ? (
          <img src={url} alt="" className="h-full w-full object-cover" />
        ) : (
          <span className="px-2 text-center text-xs text-ink-600">
            이미지 없음
            <br />
            클릭 후 Ctrl+V
          </span>
        )}
      </div>
      <div className="flex items-center gap-2">
        <input
          ref={inputRef}
          type="file"
          // 의도적으로 HEIC/HEIF 를 목록에서 뺀다: iOS 는 accept 에 heic 이 없으면 사진 선택 시점에
          // HEIC -> JPEG 자동 변환해 전달한다. 반대로 heic 을 넣으면 원본 HEIC 이 그대로 오는데,
          // Safari 는 웹에서 HEIC 디코딩을 지원하지 않아 재인코딩이 불가능하고 서버도 거절한다(이전 실패 원인).
          accept="image/jpeg,image/png,image/webp"
          className="hidden"
          onChange={handleSelect}
        />
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={busy || disabled}
          className="rounded-md border border-ink-300 px-3 py-1 text-sm hover:bg-ink-100 disabled:opacity-50"
        >
          {busy ? "업로드 중..." : value ? "이미지 변경" : "이미지 선택"}
        </button>
        {value ? (
          <button
            type="button"
            onClick={() => onChange(null)}
            disabled={busy || disabled}
            className="rounded-md border border-ink-300 px-3 py-1 text-sm text-danger-700 hover:bg-danger-50 disabled:opacity-50"
          >
            제거
          </button>
        ) : null}
      </div>
      {error ? <p className="text-xs text-danger-600">{error}</p> : null}
      <p className="text-xs text-ink-600">
        <span className="font-semibold text-ink-700">권장 크기: {RECOMMENDED_SIZE[kind]}</span>
        <br />
        JPG / PNG / WebP, 최대 5MB · 박스 클릭 후 Ctrl+V 로 캡처/복사 이미지도 첨부 가능
        <br />
        업로드 시 자동으로 적정 크기로 줄여 저장합니다(화질 유지).
      </p>
    </div>
  );
}
